// Perspective Switcher - Simple toggle for Super Admins to switch between Admin and Consultant views
// Shows "Switch to Consultant" when in Admin, "Switch to Admin" when in Consultant view

import { useNavigate } from 'react-router-dom';
import { usePerspective, AVAILABLE_PERSPECTIVES } from '../contexts/PerspectiveContext';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { ArrowRightLeft } from 'lucide-react';

export function PerspectiveSwitcher() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    activePerspective,
    switchPerspective,
    canSwitchPerspective,
  } = usePerspective();

  // Only render for Super Admins
  if (!canSwitchPerspective || user?.role !== ROLES.SUPER_ADMIN) {
    return null;
  }

  const isConsultantView = activePerspective === AVAILABLE_PERSPECTIVES.CONSULTANT;

  const handleToggle = () => {
    if (isConsultantView) {
      // Switch back to Super Admin view
      switchPerspective(AVAILABLE_PERSPECTIVES.SUPER_ADMIN);
      // Use SPA navigation so AuthContext / JWT cookie isn't re-validated via
      // a full page reload. Critical in Chrome incognito where third-party
      // cookies are blocked by default: a hard reload would drop the session
      // cookie cross-origin, /api/auth/me would 401, and the user would be
      // logged out on every perspective switch.
      navigate('/admin/dashboard');
    } else {
      // Switch to Consultant view
      switchPerspective(AVAILABLE_PERSPECTIVES.CONSULTANT);
      navigate('/consultant/dashboard');
    }
  };

  return (
    <button
      onClick={handleToggle}
      className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-teal-600 hover:bg-slate-100 rounded-md transition-colors"
      data-testid="perspective-switcher-toggle"
    >
      <ArrowRightLeft className="w-4 h-4" />
      <span>{isConsultantView ? 'Switch to Admin' : 'Switch to Consultant'}</span>
    </button>
  );
}

export default PerspectiveSwitcher;
